import {
    AssignedParticipantUnreadInfo,
    ConversationView,
    ConversationParticipantView,
    ConversationParticipantTypeEnumType,
    ConversationMemberView,
} from '@mark43/cad-api';
import {
    MessagingStoreData,
    ParticipantFiltersInfo,
    ParticipantIdentities,
    ParticipantUnreadInfo,
} from './types';
import keyBy from 'lodash/keyBy';

export const updateUnreadInfo = ({
    unreadInfoToUpdate,
    unreadCountIncrement,
    newIsStandard,
    newIsUrgent,
    newMessageId,
}: {
    unreadInfoToUpdate?: ParticipantUnreadInfo;
    unreadCountIncrement: number;
    newIsStandard?: boolean;
    newIsUrgent: boolean;
    newMessageId?: number;
}): void => {
    if (unreadInfoToUpdate === undefined) {
        return;
    }
    unreadInfoToUpdate.unreadCount += unreadCountIncrement;
    unreadInfoToUpdate.hasUrgentUnread = unreadInfoToUpdate.hasUrgentUnread || newIsUrgent;
    if (newIsStandard !== undefined) {
        unreadInfoToUpdate.hasStandardUnread =
            unreadInfoToUpdate.hasStandardUnread || newIsStandard;
    }
    if (newMessageId !== undefined) {
        unreadInfoToUpdate.oldestUnreadMessageId =
            unreadInfoToUpdate.oldestUnreadMessageId ?? newMessageId;
    }
};

export const getInitialMessagingStoreData = (existingData: Partial<MessagingStoreData> = {}) => {
    const {
        activeFilterTabMenu,
        isAddUrgentMessageEnabled = false,
        isBookmarksView = false,
        participantIdentities,
        selectedConversationFilter = {
            filterType: 'ALL_ACTIVE',
            filterParticipant: null,
        },
        selectedConversationKey,
        selectedConversationMessageList,
    } = existingData;
    return {
        activeFilterTabMenu,
        conversationsMetadata: {},
        conversationsToBeFetched: new Set<number>(),
        isAddUrgentMessageEnabled,
        isBookmarksView,
        participantIdentities,
        selectedConversationFilter,
        selectedConversationKey,
        selectedConversationMessageList,
    };
};

export const incrementAggregateUnreadCountsForNudge = (
    participantUnreadInfo: AssignedParticipantUnreadInfo,
    isUrgent: boolean,
    messageId: number,
    storeData: MessagingStoreData
): void => {
    const { participantId, entityType } = participantUnreadInfo;
    // increment unread count for the assigned participant in the conversation
    updateUnreadInfo({
        unreadInfoToUpdate: participantUnreadInfo,
        unreadCountIncrement: 1,
        newIsStandard: !isUrgent,
        newIsUrgent: isUrgent,
        newMessageId: messageId,
    });
    // increment unread count total for the participant filter
    updateUnreadInfo({
        unreadInfoToUpdate:
            storeData.participantFiltersInfo?.[buildParticipantKey(participantId, entityType)],
        unreadCountIncrement: 1,
        newIsUrgent: isUrgent,
        newMessageId: messageId,
    });
    // increment unread count total for dispatch areas filter group
    if (entityType === 'DISPATCH_AREA') {
        updateUnreadInfo({
            unreadInfoToUpdate: storeData.dispatchAreasUnreadInfo,
            unreadCountIncrement: 1,
            newIsUrgent: isUrgent,
            newMessageId: messageId,
        });
    }
    // increment unread count total for 'all' filter
    updateUnreadInfo({
        unreadInfoToUpdate: storeData.activeConversationsUnreadInfo,
        unreadCountIncrement: 1,
        newIsUrgent: isUrgent,
        newMessageId: messageId,
    });
};

export const isActiveParticipant = (
    participantKey: string,
    participantIdentities?: ParticipantIdentities
): boolean => {
    return participantIdentities?.activeMappedByKey[participantKey] !== undefined;
};

export const isActiveConversation = (
    conversation: ConversationView,
    participantIdentities: ParticipantIdentities
): boolean => {
    return conversation.assignedParticipantUnreadInfos.some(({ participantId, entityType }) => {
        return isActiveParticipant(
            buildParticipantKey(participantId, entityType),
            participantIdentities
        );
    });
};

// This function iterates through the conversations' assigned participants and produces data structures needed for UI display
// NOTE: the mid-term plan is to return this data from backend API directly to avoid this FE processing load
export const buildConversationsData = (
    conversations: ConversationView[],
    existingStoreData: MessagingStoreData
): {
    activeConversationsUnreadInfo: ParticipantUnreadInfo;
    conversationsMap: Record<number, ConversationView>;
    dispatchAreasUnreadInfo: ParticipantUnreadInfo;
    inactiveParticipantIdentities: ConversationParticipantView[];
    participantFiltersInfo: ParticipantFiltersInfo;
} => {
    // participantIdentities is persistent. (buildStoreData will always find existing participantIdentities first)
    const participantIdentities = existingStoreData.participantIdentities;

    const conversationsMap = existingStoreData.conversations ?? {};

    const activeConversationsUnreadInfo: ParticipantUnreadInfo = existingStoreData.activeConversationsUnreadInfo ?? {
        unreadCount: 0,
        hasUrgentUnread: false,
    };
    const dispatchAreasUnreadInfo: ParticipantUnreadInfo = existingStoreData.dispatchAreasUnreadInfo ?? {
        unreadCount: 0,
        hasUrgentUnread: false,
    };

    const inactiveParticipantIdentities: ConversationParticipantView[] =
        existingStoreData.participantIdentities?.inactive ?? [];

    // add metadata for 'All' filter
    const participantFiltersInfo: ParticipantFiltersInfo =
        existingStoreData.participantFiltersInfo ?? {};

    // add metadata for user's active identity filters
    participantIdentities?.active.forEach((participant) => {
        const existingParticipantFilterInfo =
            existingStoreData.participantFiltersInfo?.[
                buildParticipantKey(participant.id, participant.entityType)
            ];
        participantFiltersInfo[buildParticipantKey(participant.id, participant.entityType)] = {
            unreadCount: existingParticipantFilterInfo?.unreadCount ?? 0,
            hasUrgentUnread: existingParticipantFilterInfo?.hasUrgentUnread ?? false,
            conversationIds: existingParticipantFilterInfo?.conversationIds ?? [],
        };
    });

    conversations.forEach((conversation) => {
        const { id: conversationId, assignedParticipantUnreadInfos } = conversation;
        conversationsMap[conversationId] = conversation;

        assignedParticipantUnreadInfos.forEach((participantUnreadInfo) => {
            const {
                participantId,
                entityType,
                label,
                unreadCount,
                hasUrgentUnread,
            } = participantUnreadInfo;
            const assignedParticipant: ConversationParticipantView = {
                id: participantId,
                entityType,
                label,
            };
            const participantKey = buildParticipantKey(participantId, entityType);
            if (
                !isActiveParticipant(participantKey, participantIdentities) &&
                participantFiltersInfo[participantKey] === undefined
            ) {
                // add metadata for inactive identity filter
                participantFiltersInfo[participantKey] = {
                    unreadCount: 0,
                    hasUrgentUnread: false,
                    conversationIds: [],
                };
                if (
                    !inactiveParticipantIdentities.find(({ id }) => id === assignedParticipant.id)
                ) {
                    // inactiveParticipantIdentities gets rebuilt from scratch when processing this.data.conversations
                    // entirely. However when a single conversation is processed the previous contents of
                    // inactiveParticipantIdentitie needs to remain. To accomodate both these use cases
                    // only add the assignedParticipant to inactiveParticipantIdentities if it wasn't already present,
                    // to avoid duplicated assignedParticipants in the inactiveParticipantIdentities array.
                    inactiveParticipantIdentities.push(assignedParticipant);
                }
            }
            participantFiltersInfo[participantKey].conversationIds.push(conversationId);

            // update the aggregate unread counts for the filter bar UI
            if (isActiveParticipant(participantKey, participantIdentities)) {
                // update unread count total for the participant filter
                updateUnreadInfo({
                    unreadInfoToUpdate: participantFiltersInfo[participantKey],
                    unreadCountIncrement: unreadCount,
                    newIsUrgent: hasUrgentUnread,
                });
                // update unread count total for dispatch areas filter group
                if (entityType === 'DISPATCH_AREA') {
                    updateUnreadInfo({
                        unreadInfoToUpdate: dispatchAreasUnreadInfo,
                        unreadCountIncrement: unreadCount,
                        newIsUrgent: hasUrgentUnread,
                    });
                }
                // update unread count total for 'all' filter
                updateUnreadInfo({
                    unreadInfoToUpdate: activeConversationsUnreadInfo,
                    unreadCountIncrement: unreadCount,
                    newIsUrgent: hasUrgentUnread,
                });
            }
        });
    });
    return {
        activeConversationsUnreadInfo,
        dispatchAreasUnreadInfo,
        inactiveParticipantIdentities,
        participantFiltersInfo,
        conversationsMap,
    };
};

// this function takes the conversations parameter and builds the store data structures needed to
// display the conversations in the messaging panel UI
export const updateMessagingStoreConversationsData = ({
    conversations,
    keepExistingStoreData = false,
    messagingStoreData,
}: {
    conversations: ConversationView[];
    keepExistingStoreData: boolean;
    messagingStoreData: MessagingStoreData;
}): void => {
    if (messagingStoreData.participantIdentities === undefined) {
        throw new Error('Participant identities must be set before conversations.');
    }

    const {
        conversationsMap,
        participantFiltersInfo,
        activeConversationsUnreadInfo,
        dispatchAreasUnreadInfo,
        inactiveParticipantIdentities,
    } = buildConversationsData(
        conversations,
        keepExistingStoreData
            ? messagingStoreData
            : getInitialMessagingStoreData(messagingStoreData)
    );
    messagingStoreData.conversations = conversationsMap;
    messagingStoreData.participantFiltersInfo = participantFiltersInfo;
    messagingStoreData.activeConversationsUnreadInfo = activeConversationsUnreadInfo;
    messagingStoreData.dispatchAreasUnreadInfo = dispatchAreasUnreadInfo;
    messagingStoreData.participantIdentities.inactive = sortIdentitiesByType(
        inactiveParticipantIdentities
    );
};

export const updateMessagingStoreBookmarkedConversationsData = ({
    bookmarkedConversations,
    storeData,
}: {
    bookmarkedConversations: ConversationView[];
    storeData: MessagingStoreData;
}): void => {
    storeData.bookmarkedConversations = keyBy(bookmarkedConversations, 'id');
};

export const alphabeticalMemberSort = (a: ConversationMemberView, b: ConversationMemberView) => {
    return a.label.localeCompare(b.label);
};

export const sortIdentitiesByType = (
    participants: ConversationParticipantView[]
): ConversationParticipantView[] => {
    const typeOrder: { [key in ConversationParticipantTypeEnumType]: number } = {
        USER: 1,
        UNIT: 2,
        CALL_TAKER_STATION: 3,
        DISPATCH_AREA: 4,
        DISPATCH_AREA_RESPONDERS: 5,
    };
    return participants.sort((a, b) => {
        if (a.entityType === b.entityType) {
            return a.label.localeCompare(b.label);
        } else {
            return typeOrder[a.entityType] - typeOrder[b.entityType];
        }
    });
};

export function buildParticipantKey(
    id: number,
    participantType: ConversationParticipantTypeEnumType
) {
    return `${participantType}-${id}`;
}
